/**
 * State binding (ADR 0001, spec §10): a selector over a LoroDoc re-runs on every doc event, batched
 * per animation frame, and the component re-renders only when the selected value changes.
 * Built on `useSyncExternalStore`; the selector result is cached per subscription so React sees a
 * stable snapshot between doc events.
 */
import { useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { LoroDoc } from "loro-crdt";
import { docManager, type DocHandle } from "../sync/docManager";

const defaultEquals = <T>(a: T, b: T): boolean => Object.is(a, b) || JSON.stringify(a) === JSON.stringify(b);

/** One animation frame per doc: every selector recomputes in the same task, so React batches the
 *  resulting re-renders and no component sees one selector fresh and another stale. */
const frames = new Map<string, { raf: number; jobs: Set<() => void> }>();
function scheduleFrame(docId: string, job: () => void): void {
  let f = frames.get(docId);
  if (!f) {
    f = { raf: 0, jobs: new Set() };
    frames.set(docId, f);
  }
  f.jobs.add(job);
  if (!f.raf) {
    f.raf = requestAnimationFrame(() => {
      const jobs = [...f!.jobs];
      f!.jobs.clear();
      f!.raf = 0;
      for (const j of jobs) j();
    });
  }
}

interface Store<T> {
  subscribe(cb: () => void): () => void;
  get(): T | undefined;
  /** The selector and comparison of the latest render, for the next doc event. */
  use(selector: (doc: LoroDoc, handle: DocHandle) => T, equals: (a: T, b: T) => boolean): void;
}

function makeStore<T>(
  handle: DocHandle | null | undefined,
  first: (doc: LoroDoc, handle: DocHandle) => T,
  firstEquals: (a: T, b: T) => boolean,
): Store<T> {
  if (!handle) return { subscribe: () => () => {}, get: () => undefined, use: () => {} };
  let selector = first;
  let equals = firstEquals;
  let cached: T = selector(handle.doc, handle);
  const listeners = new Set<() => void>();
  const recompute = () => {
    const next = selector(handle.doc, handle);
    if (!equals(cached, next)) {
      cached = next;
      for (const l of listeners) l();
    }
  };
  let unsubDoc: (() => void) | null = null;
  return {
    subscribe(cb) {
      listeners.add(cb);
      if (!unsubDoc)
        unsubDoc = docManager.subscribeDoc(handle.docId, () => scheduleFrame(handle.docId, recompute));
      return () => {
        listeners.delete(cb);
        if (!listeners.size && unsubDoc) {
          unsubDoc();
          unsubDoc = null;
        }
      };
    },
    get: () => cached,
    use(s, e) {
      selector = s;
      equals = e;
    },
  };
}

/**
 * Select from a doc. Callers pass inline functions, so the selector's identity says nothing; a doc event
 * always runs the latest one. `deps` lists everything else the selector reads (props, state, the current
 * minute): when one changes, the value is selected again at once instead of at the next doc event, or never
 * (bug hunt D5: a widget's new filter did nothing until reload). Values are compared as JSON.
 */
export function useDocSelector<T>(
  handle: DocHandle | null | undefined,
  selector: (doc: LoroDoc, handle: DocHandle) => T,
  deps: readonly unknown[] = [],
  equals: (a: T, b: T) => boolean = defaultEquals,
): T | undefined {
  const depsKey = JSON.stringify(deps);
  // rebuilt (and selecting at once) when the doc or one of `deps` changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const store = useMemo(() => makeStore(handle, selector, equals), [handle?.docId, depsKey]);
  useLayoutEffect(() => store.use(selector, equals));
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

/** Open a doc for the lifetime of the component; null until restored from IndexedDB. */
export function useDoc(docId: string | null): DocHandle | null {
  const [state, setState] = useState<{ docId: string; handle: DocHandle } | null>(null);
  useEffect(() => {
    let alive = true;
    if (!docId) return;
    void docManager.open(docId).then((h) => {
      if (alive) setState({ docId, handle: h });
    });
    return () => {
      alive = false;
    };
  }, [docId]);
  return state && state.docId === docId ? state.handle : null;
}

/** Handle flags change in place; select them so a component re-renders when they do. */
export function useDocState(handle: DocHandle | null | undefined): {
  settled: boolean;
  readOnly: boolean;
  pendingCount: number;
} {
  return (
    useDocSelector(handle, (_doc, h) => ({
      settled: h.settled,
      readOnly: h.readOnly,
      pendingCount: h.pendingCount,
    })) ?? {
      settled: false,
      readOnly: false,
      pendingCount: 0,
    }
  );
}
